import { CalendarIcon, ClockIcon, FlagIcon, PlusIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, getPaymentLineItems, isGiftPurchaseItem, isMembershipItem } from "common";
import { Dayjs } from "dayjs";
import { sumBy } from "lodash";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Permission, Purchase, PurchaseItem, PurchaseItemObject, PurchaseObject, Traveler } from "types";
import { useTraveler } from "../../../../../hooks/resources/useTraveler";
import { useAuthorization } from "../../../../../hooks/useAuthorization";
import { getDisplayItem, isPackageItem } from "../../../../../models/Purchase";
import { getArrivalTime } from "../../../../../models/PurchaseItem";
import { Currency } from "../../../../Common/Currency";
import { NoTranslate } from "../../../../Common/NoTranslate";
import { PurchaseProductImage } from "../../../../PurchasesMenu/PurchaseProductImage";
import { StatusIcon } from "../../../../PurchasesMenu/PurchaseStatusIcon";
import { TimelineItem, TimelinePill } from "../../../../PurchasesMenu/Timeline/TimelineItem";
import { usePurchaseSearchStore } from "@/stores/purchaseSearchStore";

interface PaymentTimelineItemProps {
    change: Purchase;
    showDate?: boolean;
    contact?: Traveler;
}

export const PaymentTimelineItem = ({ change: purchase, showDate = false, contact }: PaymentTimelineItemProps) => {
    const history = useHistory();
    const { isGranted } = useAuthorization();
    const [setSearchQuery] = usePurchaseSearchStore((state) => [state.setSearchQuery]);

    const [setContactPurchaseId] = usePurchaseSearchStore((state) => [state.setContactPurchaseId]);

    const audit = {
        createdAt: purchase.createdAt,
        createdBy: purchase.createdBy,
    };
    const isMist = purchase.items.length > 1;
    const paymentLineItems = getPaymentLineItems(purchase);
    const purchaseAmount = Math.abs(sumBy(paymentLineItems, "amount"));
    const isGrantedPurchaseView = isGranted(Permission.PURCHASES_VIEW);
    const item = purchase.items[0];
    const path = isMist ? `/purchases/${purchase.id}` : `/purchases/${purchase.id}/${item?.id}`;
    const isPurchasePage = useMemo(() => history.location.pathname.startsWith("/purchases"), [history]);

    const handlePurchaseItemClick = async () => {
        // Removing the search query as we are redirecting to the purchase page if it has any search query we need to clear it
        if (isPurchasePage) {
            setContactPurchaseId(purchase.id);
        } else {
            setSearchQuery("");
        }

        history.push(path);
    };

    const getTitle = () => {
        if (contact?.id === purchase.organizer?.id) {
            if (!isMist && isGiftPurchaseItem(item)) {
                return "New Gift Purchase";
            }

            if (!isMist && isMembershipItem(item)) {
                return "New Membership Purchase";
            }

            return "New Purchase";
        }

        return "Attending";
    };

    return (
        <TimelineItem
            title={getTitle()}
            audit={audit}
            icon={<PlusIcon />}
            color="primary"
            showDate={showDate}
            purchase={purchase}
            badge={<StatusIcon displayText displayTextForAllStatus item={getDisplayItem(purchase)} />}
        >
            <div
                className={clsx("flex items-center", isGrantedPurchaseView && "cursor-pointer")}
                onClick={isGrantedPurchaseView ? handlePurchaseItemClick : undefined}
            >
                <TimelinePill className="relative">
                    {isMist && <PurchaseOrganizer purchase={purchase} />}

                    <div className="flex items-center space-x-4 p-2">
                        <div className="flex min-w-72 items-center">
                            <div className="relative mr-3 flex">
                                <PurchaseProductImage purchase={purchase} />
                            </div>
                            <PurchaseDetails purchase={purchase} />
                        </div>
                        <h2 className="ml-4">
                            <Currency shouldRemoveTrailingZeroes={false}>{purchaseAmount}</Currency>
                        </h2>
                    </div>
                </TimelinePill>
            </div>
        </TimelineItem>
    );
};

PaymentTimelineItem.supportsAudit = (purchase: Purchase) => {
    return purchase?.object === PurchaseObject.PURCHASE;
};

interface PurchaseDetailsProps {
    purchase: { items: PurchaseItem[] };
}

export const PurchaseDetails = ({ purchase }: PurchaseDetailsProps) => {
    const item = purchase.items[0];
    const isPackage = isPackageItem(item);
    const isMist = purchase.items.length > 1;
    const purchaseName = isPackage ? purchase.items[0].parentProductName : purchase.items[0].name;
    const arrivalDateTime: Dayjs | null = "arrivalDateTime" in item ? item.arrivalDateTime : null;
    const arrivalDate = arrivalDateTime ? formatDate(arrivalDateTime, DateFormat.DATE_LONG) : null;
    const arrivalTime = "arrivalDateTime" in item ? getArrivalTime(item) : null;
    const isGift = item.object === PurchaseItemObject.GIFT;
    const isMembership = isMembershipItem(item);
    const hasSchedule = arrivalDate ?? arrivalTime;

    return (
        <div>
            <NoTranslate as="h5" className="font-bold">
                {purchaseName}
            </NoTranslate>

            {isGift || isMembership || isMist || !hasSchedule ? null : (
                <div className="mt-1 flex space-x-2">
                    <span className="flex items-center space-x-1">
                        <CalendarIcon />
                        <NoTranslate>{arrivalDate}</NoTranslate>
                    </span>
                    {arrivalTime ? (
                        <span className="flex items-center space-x-1">
                            <ClockIcon />
                            <NoTranslate>{arrivalTime}</NoTranslate>
                        </span>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export const PurchaseOrganizer = ({ purchase }: { purchase: Purchase }) => {
    const { isGranted } = useAuthorization();
    const { data: organizer } = useTraveler(purchase.organizer?.id, null, {
        enabled: isGranted(Permission.MODIFY_PURCHASE_CONTACT_VIEW),
    });

    return (
        <>
            <div className="flex space-x-1 pt-2 pl-2">
                <Tooltip content="Main attendee">
                    <FlagIcon size="medium" />
                </Tooltip>

                <NoTranslate as="h5" className="font-bold">
                    {organizer?.name}
                </NoTranslate>
            </div>
            <hr className="my-2 text-gray-light" />
        </>
    );
};
